"use client";

import Grid from "@mui/material/Grid";
import Rating from "@mui/material/Rating";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent"; // MUI ICON COMPONENTS

import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import Remove from "@mui/icons-material/Remove"; // GLOBAL CUSTOM COMPONENTS

import { FlexBox } from "../flex-box";
import { Carousel } from "../carousel";
import BazaarImage from "../Image";
import { H1, H2, H3, H6, Paragraph } from "../Typography"; // LOCAL CUSTOM HOOKS

import useCart from "../../hooks/useCart"; // CUSTOM UTILS LIBRARY FUNCTION
import { formatMoney, toAssetImages } from "src/lib/utils";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { useRef } from "react";
import { styled } from "@mui/material/styles";

// =====================================================
const StyledInput = styled("input")(() => ({
  width: "100%",
  border: "none",
  outline: "none",

  "&:focus": {
    border: "none",
    outline: "none",
  },
}));

const ProductViewDialog = (props) => {
  const { product, openDialog, handleCloseDialog } = props;
  const { state, dispatch } = useCart();
  const cartItem = state.cart.find((item) => item.product.id === product.id);
  const inputRef = useRef();

  const handleCartAmountChange = (amount) => {
    dispatch({
      type: "CHANGE_CART_AMOUNT",
      payload: {
        product,
        quantity: amount,
      },
    });
  };

  return (
    <Dialog
      open={openDialog}
      maxWidth={false}
      onClose={handleCloseDialog}
      sx={{
        zIndex: 1501,
      }}
    >
      <DialogContent
        sx={{
          maxWidth: 900,
          width: "100%",
        }}
      >
        <div>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <Carousel
                slidesToShow={1}
                arrowStyles={{
                  boxShadow: 0,
                  color: "primary.main",
                  backgroundColor: "transparent",
                }}
              >
                {product.medias &&
                  product.medias.length > 0 &&
                  product.medias.map((item, index) => (
                    <BazaarImage
                      key={index}
                      src={toAssetImages(item.value)}
                      alt="product"
                      sx={{
                        mx: "auto",
                        width: "100%",
                        objectFit: "contain",
                        height: {
                          sm: 400,
                          xs: 250,
                        },
                      }}
                    />
                  ))}
              </Carousel>
            </Grid>

            <Grid item md={6} xs={12} alignSelf="center">
              <H2>{product.name}</H2>
              {product.category && (
                <Paragraph py={1} color="grey.500" fontWeight={600} fontSize={13}>
                  <SalifyFallback.Label path="Category" default="Danh mục" />:{" "}
                  {product.category.name}
                </Paragraph>
              )}
              <H1 color="primary.main">{formatMoney(product.salePrice)}</H1>

              <FlexBox alignItems="center" gap={1} mt={1}>
                <Rating color="warn" value={product.finalRating} readOnly />
                <H6 lineHeight="1">(50)</H6>
              </FlexBox>

              <Paragraph my={2}>{product.description}</Paragraph>

              <Divider
                sx={{
                  mb: 2,
                }}
              />

              <FlexBox alignItems="center">
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    p: ".6rem",
                    height: 45,
                  }}
                  onClick={() => {
                    inputRef.current.value = (+inputRef.current?.value ?? 0) - 1;
                  }}
                >
                  <Remove fontSize="small" />
                </Button>

                <H3 fontWeight="600" mx={2.5}>
                  <StyledInput
                    size={2}
                    ref={inputRef}
                    defaultValue={+cartItem?.quantity || 1}
                    onChange={(e) => {
                      inputRef.current.value = +(e.target.value || 0);
                    }}
                  />
                </H3>

                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    p: ".6rem",
                    height: 45,
                  }}
                  onClick={() => {
                    inputRef.current.value = (+inputRef.current?.value ?? 0) + 1;
                  }}
                >
                  <Add fontSize="small" />
                </Button>
              </FlexBox>
              <Button
                size="large"
                color="primary"
                variant="contained"
                onClick={() => {
                  handleCartAmountChange(+inputRef.current?.value);
                  handleCloseDialog();
                }}
                sx={{
                  height: 45,
                  marginTop: 2,
                }}
              >
                <SalifyFallback.Label path="Add To Cart" default="Thêm vào Giỏ hàng" />
              </Button>
            </Grid>
          </Grid>
        </div>

        <IconButton
          sx={{
            position: "absolute",
            top: 3,
            right: 3,
          }}
          onClick={handleCloseDialog}
        >
          <Close fontSize="small" color="secondary" />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};

export default ProductViewDialog;
